import {AbstractStripePayment, type ConfirmResult, type TokenResult} from './abstract/class-abstract-stripe-payment';
import {StripeCardElement} from './elements/class-stripe-card-element';
import {type Stripe, type StripeElements} from '@stripe/stripe-js';
import {createSetupIntent} from './setup-intent';
import {type PeachPayCheckoutData} from '../types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

class StripeCardPayment extends AbstractStripePayment {
	private readonly $cardElement: Readonly<StripeCardElement>;

	constructor(stripe: Stripe, elements: StripeElements) {
		super(stripe, elements);

		this.$cardElement = new StripeCardElement(this.elements);
	}

	public override init() {
		this.$cardElement.mount();
	}

	public gatewayId() {
		return 'peachpay_stripe_card';
	}

	public async tokenize(): Promise<TokenResult> {
		const [paymentMethodId, update] = await this.$cardElement.tokenize(this.stripe);
		if (!paymentMethodId) {
			return [null, update];
		}

		let payDetails;
		if (peachpay_checkout_data.page.is_order_pay && peachpay_checkout_data.order_pay_details) {
			payDetails = peachpay_checkout_data.order_pay_details;
		} else {
			payDetails = peachpay_checkout_data.cart_details;
		}

		if (!payDetails.is_only_free_trial) {
			return [paymentMethodId, {}];
		}

		const clientSecret = await createSetupIntent('card', paymentMethodId);
		if (!clientSecret) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to create setup intent.',
			}];
		}

		const {error: confirmError, setupIntent: confirmIntent} = await this.stripe.confirmCardSetup(clientSecret);
		if (!confirmIntent || confirmError) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: confirmError?.message ?? 'Failed to confirm setup intent.',
			}];
		}

		if (confirmIntent.status !== 'succeeded') {
			return [null, {
				paymentStatus: 'creationfailed',
				note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
			}];
		}

		return [confirmIntent.payment_method as string, {}];
	}

	public async confirm(clientSecret: string): Promise<ConfirmResult> {
		const {paymentIntent, error} = await this.stripe.retrievePaymentIntent(clientSecret);

		if (!paymentIntent || error) {
			const message = error?.message ?? 'Failed to retrieve payment intent.';
			return [null, {
				note: message,
			}];
		}

		if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
			return ['success', {}];
		}

		if (paymentIntent.status === 'requires_action') {
			const {paymentIntent, error} = await this.stripe.confirmCardPayment(clientSecret);
			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to confirm payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
				return ['success', {}];
			}
		}

		return [null, {
			note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
		}];
	}

	public override async setupToken(): Promise<TokenResult> {
		const [paymentMethodId, update] = await this.$cardElement.tokenize(this.stripe);
		if (!paymentMethodId) {
			return [null, update];
		}

		const clientSecret = await createSetupIntent('card', paymentMethodId);
		if (!clientSecret) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: 'Failed to create setup intent.',
			}];
		}

		const {error: confirmError, setupIntent: confirmIntent} = await this.stripe.confirmCardSetup(clientSecret);
		if (!confirmIntent || confirmError) {
			return [null, {
				paymentStatus: 'creationfailed',
				note: confirmError?.message ?? 'Failed to confirm setup intent.',
			}];
		}

		if (confirmIntent.status !== 'succeeded') {
			return [null, {
				paymentStatus: 'creationfailed',
				note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
			}];
		}

		return [confirmIntent.payment_method as string, {}];
	}
}

export {
	StripeCardPayment,
};
